import React, { useState } from 'react';
import { ModalView, ModalViewProps } from '@cp/ds/src/components/modalView';
import { ApiServerEntityIDType } from '@cp/utils/apiServer/types';
import { Box } from '@cp/ds/src/components/box';
import { Link } from '@cp/ds/src/components/link';
import { Typography } from '@cp/ds/src/components/typography';
import { AdminCatalogItemViewModel } from '@common/model/catalog/item/admin.view';
import { formatVacancyDates } from '@cp/entities/vacancy';
import { ButtonGroup } from '@cp/ds/src/components/buttonGroup';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { Declination } from '@cp/shared/declination';
import { useCareerDeclination } from '@cp/entities/career';
import { Container } from '@cp/ds/src/components/container';
import { Button } from '@cp/ds/src/components/button';
import { Skeleton } from '@cp/ds/src/components/skeleton';
import { ResponsesListFeature } from 'src/features/responseList';
import { useDialogViewContext, useOpenDialog } from 'src/features/dialog/view';
import { OpenVacancyLink, VacancyCard, VacancyDeleteButton } from 'src/features/vacancy';
import { AutoSelectionTag } from 'src/entities/vacancy';
import { MemberUser } from '@youhelp-ru/core';
import { useClientWebResolver } from '@cp/entities/clientWebRoutes';
import { BotIdentificator } from '@common/model/bot';
import { generateCatalogItemPermalink } from '../lib';
import { CopyButton } from './copyButton';

export interface MatchingModalProps {
  catalogItemId: ApiServerEntityIDType;
  vacancy?: AdminCatalogItemViewModel;
}

export const MatchingModal: React.FC<MatchingModalProps & Pick<ModalViewProps, 'isOpen' | 'onClose'>> = ({
  catalogItemId,
  isOpen,
  vacancy,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<'responses' | 'edit'>(vacancy?.matchedAt ? 'responses' : 'edit');
  const { openDialog, ...viewerProps } = useOpenDialog();
  const { Viewer } = useDialogViewContext();
  const theme = useTheme();
  const declination = useCareerDeclination();
  const pathGenerator = useClientWebResolver();
  const permalink = vacancy
    ? generateCatalogItemPermalink(vacancy, (r) => String(pathGenerator.resolve(r)), { CITY_PATH_PARAMETER: 'city' })
    : '';
  const profile = vacancy?.clientProfile;
  return (
    <ModalView
      buttons={
        <Container sx={{ display: 'flex', gap: 2 }}>
          <Button color="white" disabled>
            Редактировать
          </Button>
          <VacancyDeleteButton itemId={catalogItemId} />
          <CopyButton color="white" copyText={permalink} />
        </Container>
      }
      config={{ desktop: 'drawerRight', mobile: 'drawerRight' }}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Box sx={{ display: 'flex', width: '560px', flexDirection: 'column', gap: 2.5, m: 5 }}>
        <Box>
          {vacancy ? (
            <Typography component="h2" sx={{ mb: 1, mt: 0 }} variant="h2">
              Найти {declination(1, vacancy.careerId, Declination.Accusative)} в {vacancy.city?.translation.namePrepositional}
            </Typography>
          ) : (
            <Skeleton>
              <Typography sx={{ mb: 1 }} variant="h2">
                Найти няню в Москве
              </Typography>
            </Skeleton>
          )}
          {vacancy ? (
            <Typography variant="body bold">{formatVacancyDates(vacancy)}</Typography>
          ) : (
            <Skeleton>
              <Typography variant="body bold">Vacancy dates</Typography>
            </Skeleton>
          )}
          <Box>
            {profile && (
              <Link href={`tel://${profile.phone}`}>
                {profile.phone} ({profile.name})
              </Link>
            )}
          </Box>
          {vacancy ? <AutoSelectionTag data={vacancy} sx={{ mt: 1 }} /> : null}
        </Box>
        <ButtonGroup
          items={[
            { label: 'Отклики', value: 'responses' },
            { label: 'Вакансия', value: 'edit' },
          ]}
          name="show"
          onChange={setActiveTab}
          size="small"
          sx={{ background: theme.palette.background.default }}
          value={activeTab}
        />
        {activeTab === 'responses' ? (
          <ResponsesListFeature
            catalogItemId={catalogItemId}
            onItemClick={(m) => openDialog({ clientId: m.respondentId })}
            renderItemContent={(m) => {
              return (
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      const as = profile?.id ? MemberUser.client(profile.id) : null;
                      openDialog({ id: m.dialogId }, as);
                    }}
                    size="extraSmall"
                    variant="secondary"
                  >
                    Наниматель
                  </Button>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      openDialog({ id: m.dialogId }, MemberUser.client(m.respondentId));
                    }}
                    size="extraSmall"
                    variant="secondary"
                  >
                    Cпециалист
                  </Button>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      openDialog({ id: m.dialogId }, MemberUser.bot(BotIdentificator.SupportBot));
                    }}
                    size="extraSmall"
                    variant="secondary"
                  >
                    ТП
                  </Button>
                </Box>
              );
            }}
          />
        ) : null}
        <Box sx={{ display: activeTab === 'edit' ? 'block' : 'none' }}>
          {vacancy && (
            <VacancyCard model={vacancy}>
              <Box sx={{ my: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                <OpenVacancyLink model={vacancy}>Открыть на сайте</OpenVacancyLink>
              </Box>
            </VacancyCard>
          )}
        </Box>
      </Box>
      <Viewer {...viewerProps} />
    </ModalView>
  );
};
